// Lib
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Handsontable from 'handsontable/base';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect, useDispatch } from 'react-redux';
import classNames from 'classnames';

import { LEGACY_MILANOTE_CONTEXT_MENU_PLUGIN_NAME } from './modules/MilanoteContextMenuPlugin';

import MilanoteCellEditorComponent from './modules/MilanoteCellEditorComponent';

import hyperFormulaInstance from './manager/hyperFormulaInstance';

// Config
import getClientConfig from '../../../utils/getClientConfig';

// Components
import useTableCellEditingHandlers from './handlers/useTableCellEditingHandlers';
import TableOverlay from './handlers/TableOverlay';
import TablePopups from './TablePopups';
import HotTableRendererPortalManager from './manager/HotTableRendererPortalManager';
import useTableStateSyncHandlers from './handlers/useTableStateSync';
import useTableSelectionHandlers from './handlers/useTableSelectionHandlers';
import useTableColumnResizeHandlers from './handlers/useTableColumnResizeHandlers';
import useTableResizeEffects from './handlers/useTableResizeEffects';
import useTableMoveColumnRowHandlers from './handlers/useTableMoveColumnRowHandlers';
import useTableContextMenuHandlers from './handlers/useTableContextMenu';
import useTableSelectionEffects from './handlers/useTableSelectionEffects';
import useTableColumnResizeEffects from './handlers/useTableColumnResizeEffects';
import useTableStateSyncEffects from './handlers/useTableStateSyncEffects';
import useTableCellEditingEffects from './handlers/useTableCellEditingEffects';
import useManualColumnResize from './handlers/useManualColumnResize';
import { useSyncLegacyActiveTableState } from '../useSyncLegacyActiveTableState';
import { useTableEditingHandlers } from './handlers/useTableEditingHandlers';

// Utils
import { markEventAsHandled } from '../../../utils/react/reactEventUtil';
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { getCellValueArray, sanitizeClipboardHtmlForPaste } from './utils/tableDataUtils';
import { convertColWidthsGUtoPx, getColWidthsResizeWidth } from './utils/tableSizeUtils';

// Selectors
import {
    getTableContentColWidthsGUSelector,
    getTableContentDataSelector,
    isCurrentlyEditingTableCell as selectIsCurrentlyEditingTableCell,
} from './tableSelector';
import { getThemeIsDarkMode } from '../../../user/account/accountModal/interface/themeSettings/themeSelector';
import { getCurrentlyEditingEditorIdForElement } from '../../selection/currentlyEditingSelector';
import { getIsRedoing, getIsUndoing } from '../../../utils/undoRedo/undoRedoSelector';
import { userCurrencyPreferenceSelector, userLanguagePreferenceSelector } from '../../../user/currentUserSelector';

// Types
import { TableAxis } from '../../../../common/table/TableTypes';
import { LEGACY_MILANOTE_EDITING_PLUGIN_NAME } from './modules/MilanoteEditingPlugin';
import { LEGACY_MILANOTE_FORMULA_CELL_REFERENCE_PLUGIN_NAME } from './modules/MilanoteFormulaCellReferencePlugin';
import { LEGACY_MILANOTE_FORMULA_AUTOCOMPLETE_PLUGIN_NAME } from './modules/MilanoteFormulaAutocompletePlugin';
import { MILANOTE_FORMULA_CELL_REFERENCE_PLUGIN_NAME } from '../modules/MilanoteFormulaCellReferencePlugin';
import { MILANOTE_FORMULA_AUTOCOMPLETE_PLUGIN_NAME } from '../modules/MilanoteFormulaAutocompletePlugin';
import { MILANOTE_EDITING_PLUGIN_NAME } from '../modules/MilanoteEditingPlugin';
import { MILANOTE_EDITING_CLEANUP_PLUGIN_NAME } from '../modules/MilanoteEditingCleanupPlugin';
import { LEGACY_MILANOTE_EDITING_CLEANUP_PLUGIN_NAME } from './modules/MilanoteEditingCleanupPlugin';
import { MILANOTE_CONTEXT_MENU_PLUGIN_NAME } from '../modules/MilanoteContextMenuPlugin';

// Styles
import './HotTable.scss';
import './HotTableRefHeaders.scss';
import './HotContextMenu.scss';

const config = getClientConfig();

// FIXME - It looks like this creates a new selector on every call, which effectively disables memoization.
//  Instead if it used a "createMapStateToProps" pattern that returned a new function it would work correctly.
const mapStateToProps = (state, { elementId }) =>
    createStructuredSelector({
        tableContentData: getTableContentDataSelector(),
        tableContentColWidthsGU: getTableContentColWidthsGUSelector(),
        currentlyEditingEditorId: getCurrentlyEditingEditorIdForElement(elementId),
        isCurrentlyEditingTableCell: selectIsCurrentlyEditingTableCell(elementId),
        isDarkMode: getThemeIsDarkMode,
        isUndoingOrRedoing: (state) => getIsUndoing(state) || getIsRedoing(state),
        locale: userLanguagePreferenceSelector,
        currencyPreference: userCurrencyPreferenceSelector,
    });

// NOTE: "props" is defined below to avoid having to change all the usages within this file after the
//  hotTableContainerRef was moved into this component.
const HotTable = (ownProps) => {
    const {
        element,
        elementId,
        tableContentData,
        tableContentColWidthsGU,
        hotTableInstanceRef,
        gridSize,
        isDarkMode,
        inList,
        isReadOnly,
        isResizing,
        isCurrentlyEditingTableCell,
        getContextZoomScale,
        showTitle,
        showCaption,
        locale,
        filterQuery,
        isSingleSelected,
        currentCellSelections,
        isEditing,
        dispatchStartEditingTitle,
        dispatchStartEditingTableCell,
        dispatchStartEditingTableGrid,
    } = ownProps;

    const [mounted, setMounted] = useState(false);
    const [currentlyEditingCellEditorCoords, setCurrentlyEditingCellEditorCoords] = useState(null);

    const dispatch = useDispatch();
    const cellEditorRef = useRef(null);
    const dropdownRef = useRef(null);

    const hotTableContainerRef = useRef(null);
    const hotTableRendererPortalManagerRef = useRef(null);

    // The hotTableContainerRef needed to be pulled into this component to avoid issues when switching
    //  the versions of the Hot instance. Otherwise, the ref would be shared between the old and the new
    //  versions and it would cause React errors when unmounting sub-components.
    const props = {
        ...ownProps,
        hotTableContainerRef,
    };

    const isActive = isEditing || isSingleSelected;

    const { jumpToCaption, jumpToTitle, startEditingCellEditor, finishEditingCellEditor } = useTableEditingHandlers({
        hotTableInstanceRef,
        showTitle,
        showCaption,
        elementId,
        isCurrentlyEditingTableCell,
        dispatchStartEditingTitle,
        dispatchStartEditingTableCell,
        dispatchStartEditingTableGrid,
        setCurrentlyEditingCellEditorCoords,
    });

    const tableCellEditingHandlers = useTableCellEditingHandlers(props);
    const tableCellSelectionHandlers = useTableSelectionHandlers(props);
    const tableColumnResizeHandlers = useTableColumnResizeHandlers(props);
    const tableMoveColumnRowHandlers = useTableMoveColumnRowHandlers(props);
    const tableContextMenuHandlers = useTableContextMenuHandlers(props);
    const tableStateSyncHandlers = useTableStateSyncHandlers(props);

    // Initialize Handsontable instance
    useEffect(() => {
        const tableContainer = hotTableContainerRef.current;
        const resizeToWidth = getColWidthsResizeWidth(inList, hotTableContainerRef, getContextZoomScale);
        const tableContentColWidthsPx = convertColWidthsGUtoPx(tableContentColWidthsGU, gridSize, resizeToWidth);

        hotTableInstanceRef.current = new Handsontable(tableContainer, {
            licenseKey: config.handsontable?.licenseKey,
            data: getCellValueArray(tableContentData),

            editor: isReadOnly ? null : 'milanoteCellEditorLegacy',
            renderer: hotTableRendererPortalManagerRef.current.getRendererWrapper(),

            colHeaders: true,
            rowHeaders: true,
            autoColumnSize: false,
            autoRowSize: false,
            autoWrapRow: true,
            manualRowMove: true,
            manualColumnMove: true,
            columnHeaderHeight: 0,
            rowHeaderHeight: 0,
            height: 'auto',
            formulas: { engine: hyperFormulaInstance, sheetName: elementId },
            manualColumnResize: tableContentColWidthsPx,
            manualRowResize: true,
            contextMenu: tableContextMenuHandlers.getTableContextMenuConfig,
            outsideClickDeselects: false,
            colWidths: tableColumnResizeHandlers.getDefaultColWidth,
            rowHeights: tableColumnResizeHandlers.getDefaultRowHeight,

            sanitizer: sanitizeClipboardHtmlForPaste,

            // NOTE: Needs to be in the function format to avoid the `this` context being lost
            afterInit() {
                // This is a hack to get the table to get handsontable context menu event only triggered when there are
                // cell selections
                this.getPlugin(LEGACY_MILANOTE_CONTEXT_MENU_PLUGIN_NAME).removeHotContextMenuEventListeners();

                // This is a hack to force handsontable to open keyboard on cell selection on the iPad.
                // Handsontable, by default, does not do this: https://github.com/handsontable/handsontable/pull/5529
                const copyPastePluginInstance = this.getPlugin('CopyPaste');
                if (copyPastePluginInstance?.focusableElement?.focus && !isReadOnly) {
                    copyPastePluginInstance.focusableElement.focus = function () {
                        this.mainElement.value = ' ';
                        this.mainElement.select();
                    };
                }
            },

            afterViewRender: (...args) => hotTableRendererPortalManagerRef.current.afterViewRender(...args),

            beforeChange: tableCellEditingHandlers.beforeChange,
            afterChange: tableCellEditingHandlers.afterChange,
            afterCreateRow: tableCellEditingHandlers.afterGridInsert(TableAxis.ROW),
            afterCreateCol: tableCellEditingHandlers.afterGridInsert(TableAxis.COL),
            afterRemoveRow: tableCellEditingHandlers.afterGridRemove(TableAxis.ROW),
            afterRemoveCol: tableCellEditingHandlers.afterGridRemove(TableAxis.COL),

            beforeAutofill: tableCellEditingHandlers.beforeAutofill,

            beforeCopy: tableCellEditingHandlers.beforeCopy,
            beforeCut: tableCellEditingHandlers.beforeCut,
            beforePaste: tableCellEditingHandlers.beforePaste,

            beforeOnCellMouseDown: (...args) => {
                tableCellEditingHandlers.beforeOnCellMouseDown(...args);
                tableMoveColumnRowHandlers.beforeOnCellMouseDown(...args);
            },

            afterRowMove: tableCellEditingHandlers.afterGridMovement(TableAxis.ROW),
            afterColumnMove: tableCellEditingHandlers.afterGridMovement(TableAxis.COL),

            afterSelection: tableCellSelectionHandlers.afterSelection,
            afterSelectionEnd: tableCellSelectionHandlers.afterSelectionEnd,
            afterDeselect: tableCellSelectionHandlers.afterDeselect,

            afterColumnResize: tableColumnResizeHandlers.afterColumnResize,

            cellEditorRef,
            [LEGACY_MILANOTE_EDITING_PLUGIN_NAME]: {
                jumpToCaption,
                jumpToTitle,
                isReadOnly,
                elementId,
                dispatch,
            },
            [LEGACY_MILANOTE_EDITING_CLEANUP_PLUGIN_NAME]: true,
            [LEGACY_MILANOTE_FORMULA_CELL_REFERENCE_PLUGIN_NAME]: {
                cellEditorRef,
            },
            [LEGACY_MILANOTE_FORMULA_AUTOCOMPLETE_PLUGIN_NAME]: {
                enabled: true,
                cellEditorRef,
                dropdownRef,
            },
            [LEGACY_MILANOTE_CONTEXT_MENU_PLUGIN_NAME]: true,

            // DISABLE NEW PLUGINS
            [MILANOTE_FORMULA_CELL_REFERENCE_PLUGIN_NAME]: false,
            [MILANOTE_FORMULA_AUTOCOMPLETE_PLUGIN_NAME]: false,
            [MILANOTE_EDITING_PLUGIN_NAME]: false,
            [MILANOTE_EDITING_CLEANUP_PLUGIN_NAME]: false,
            [MILANOTE_CONTEXT_MENU_PLUGIN_NAME]: false,
        });

        hotTableInstanceRef.current.milanoteProps = {
            gridSize,
            isDarkMode,
            showTitle,
            showCaption,
            getContextZoomScale,
            hotTableContainerRef,
            isResizing,
            elementId: getElementId(element),
            locale,
            filterQuery,
            startEditingCellEditor,
            finishEditingCellEditor,
        };

        setMounted(true);

        return () => {
            const currentHotInstance = hotTableInstanceRef.current;

            // HOT CopyPaste plugin add paste event listeners on `document`.
            // When a late paste event occurs after Handsontable instance is destroyed,
            // this could cause an error as it will try to unsafely access `getActiveEditor()` from hotInstance.
            currentHotInstance?.getPlugin('CopyPaste')?.disablePlugin();

            // Destroy the hot table instance.
            // NOTE: We need a reference to the current instance as the hotTableInstanceRef prior to the setTimeout
            //   because the ref is defined within Table.jsx and it may change when switching between table versions.
            //   This is also performed in a setTimeout to allow React to finish unmounting components that
            //   may be using the hot instance before we destroy it.
            setTimeout(() => {
                currentHotInstance?.destroy();
            });
        };
    }, []);

    useSyncLegacyActiveTableState(hotTableInstanceRef.current, isActive);

    // These need to be called after the effect that initializes the hotTableInstanceRef
    useTableCellEditingEffects(props);
    useTableSelectionEffects(props);
    useTableColumnResizeEffects(props);
    useTableResizeEffects(props);
    useTableStateSyncEffects({ ...props, tableStateSyncHandlers });
    useManualColumnResize({ ...props, hotTableContainerRef });

    const onMouseDown = useCallback(
        (event) => {
            // This is done in order to stop the parent element (in ElementContainer) to handle this event when other areas of
            // the table is clicked.
            isSingleSelected && markEventAsHandled(event);
        },
        [isSingleSelected],
    );

    const nRows = hotTableInstanceRef.current?.countRows();
    const nCols = hotTableInstanceRef.current?.countCols();

    const isSelectedLastRow = useMemo(
        () =>
            !!currentCellSelections &&
            currentCellSelections.some(([startRow, , endRow]) => startRow === nRows - 1 || endRow === nRows - 1),
        [currentCellSelections, nRows],
    );

    const isSelectedLastCol = useMemo(
        () =>
            !!currentCellSelections &&
            currentCellSelections.some(([, startCol, , endCol]) => startCol === nCols - 1 || endCol === nCols - 1),
        [currentCellSelections, nCols],
    );

    return (
        <div
            className={classNames('HotTable LegacyHotTable', {
                'is-selected-last-col': isSelectedLastCol,
                'is-selected-last-row': isSelectedLastRow,
            })}
            onMouseDown={onMouseDown}
        >
            <TableOverlay {...props} />

            <TablePopups {...props} dropdownRef={dropdownRef} cellEditorRef={cellEditorRef} />

            <MilanoteCellEditorComponent
                {...props}
                cellEditorRef={cellEditorRef}
                hotTableContainerRef={hotTableContainerRef}
                hotTableInstanceRef={hotTableInstanceRef}
                gridSize={gridSize}
                currentlyEditingCellEditorCoords={currentlyEditingCellEditorCoords}
                // Pass in mounted prop to ensure that the MilanoteCellEditorComponent
                // is re-rendered after HotTable is mounted
                mounted={mounted}
            />

            <HotTableRendererPortalManager ref={hotTableRendererPortalManagerRef} />

            <div className="HotTableLib" ref={hotTableContainerRef}></div>
        </div>
    );
};

HotTable.propTypes = {
    element: PropTypes.object,
    elementId: PropTypes.string,
    tableContentData: PropTypes.array,
    tableContentColWidthsGU: PropTypes.array,
    dispatchUpdateTableElement: PropTypes.func,
    gridSize: PropTypes.number,
    tempSize: PropTypes.object,
    currentCellSelections: PropTypes.object,
    isPresentational: PropTypes.bool,
    isSingleSelected: PropTypes.bool,
    isEditing: PropTypes.bool,
    isResizing: PropTypes.bool,
    isDarkMode: PropTypes.bool,
    inList: PropTypes.string,
    isReadOnly: PropTypes.bool,
    showTitle: PropTypes.bool,
    showCaption: PropTypes.bool,
    isCurrentlyEditingTableCell: PropTypes.bool,
    locale: PropTypes.string,
    filterQuery: PropTypes.string,
    translateTableContextMenu: PropTypes.func,

    isGridMoving: PropTypes.bool,
    setIsGridMoving: PropTypes.func,

    dispatchSetCellSelection: PropTypes.func,
    dispatchStartEditingTableCell: PropTypes.func,
    dispatchFinishEditingElement: PropTypes.func,

    dispatchUndoAction: PropTypes.func,
    dispatchRedoAction: PropTypes.func,
    dispatchDeselectElement: PropTypes.func,
    dispatchStartEditingTitle: PropTypes.func,
    dispatchStartEditingTableGrid: PropTypes.func,

    hotTableInstanceRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    getContextZoomScale: PropTypes.func,
};

export default connect(mapStateToProps, null, null)(HotTable);
